import type { QueryClient } from '@tanstack/react-query';
import { apiClient } from '@/api/client';
import { useDialogStore } from '@/stores/dialog-store';
import { useToastStore } from '@/stores/toast-store';
import type { WorkareaTab } from '@/types';
import type { TableActionDeps } from './table-actions';

interface BuildDeps {
  connectionId: string;
  /** Database the table lives in. Passed through to every tab opened from
   *  this menu so queries route to the right per-DB pool; undefined means
   *  "the connection's default database". */
  database?: string;
  schema: string;
  table: string;
  openTab: (tab: WorkareaTab) => void;
  queryClient: QueryClient;
  /** Optional initial columns for Import Data modal prefill. */
  tableColumns?: { name: string; type: string }[];
  /** Opens the ImportModal. Currently ConnectionTree-local; InfoView's table
   *  actions fall back to a toast. */
  onShowImportModal?: (state: {
    connectionId: string;
    schema: string;
    tableName?: string;
    tableColumns?: { name: string; type: string }[];
  }) => void;
}

/**
 * Assemble TableActionDeps by wiring each action. Used by ConnectionTree
 * (right-click context menu) and StructureView (Actions panel) so both
 * render from the same source of truth.
 */
export function getTableActionDeps(opts: BuildDeps): TableActionDeps {
  const { connectionId, database, schema, table, openTab, queryClient } = opts;
  const qualifiedName = `${schema}.${table}`;
  const selectQuery = `SELECT * FROM ${qualifiedName} LIMIT 100;`;
  // Tab ids include the database so same-schema.same-table in two different
  // DBs don't collide on one tab.
  const scope = database ? `${connectionId}:${database}` : connectionId;

  const downloadSQL = (filename: string, sql: string) => {
    const blob = new Blob([sql], { type: 'text/sql' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  };

  return {
    onOpenTable: () => {
      openTab({
        type: 'table',
        id: `table:${scope}:${schema}.${table}`,
        connectionId,
        database,
        schema,
        table,
        where: '',
        orderBy: '',
      });
    },
    onViewStructure: () => {
      openTab({
        type: 'structure',
        id: `structure:${scope}:${schema}.${table}`,
        connectionId,
        database,
        schema,
        table,
      });
    },
    onModifyTable: () => {
      openTab({
        type: 'designer',
        id: `designer:${scope}:${schema}:${table}`,
        connectionId,
        database,
        schema,
        tableName: table,
        mode: 'alter',
      });
    },
    onCompareWith: () => {
      openTab({
        type: 'diff',
        id: `diff:${scope}:${schema}.${table}:${Date.now()}`,
        connectionId,
        databaseA: database,
        databaseB: database,
        schemaA: schema,
        tableA: table,
        schemaB: schema,
        tableB: table,
      });
    },
    onNewSelectQuery: () => {
      openTab({
        type: 'query',
        id: `query:${scope}:${Date.now()}`,
        connectionId,
        database,
        title: `SELECT ${table}`,
        sql: selectQuery,
      });
    },
    onReload: () => {
      queryClient.invalidateQueries({ queryKey: ['schema', connectionId, database] });
      queryClient.invalidateQueries({ queryKey: ['table-data', connectionId, database, schema, table] });
      queryClient.invalidateQueries({ queryKey: ['table-structure', connectionId, database, schema, table] });
    },
    onBackup: () => {
      apiClient
        .post<{ sql: string; rowCount: number }>(
          `/api/management/${connectionId}/backup-table`,
          { schema, table, database },
        )
        .then((result) => {
          downloadSQL(`${table}_backup.sql`, result.sql);
          useToastStore.getState().addToast('success', 'Backup downloaded');
        })
        .catch((err: unknown) => {
          const msg = err instanceof Error ? err.message : 'Backup failed';
          useToastStore.getState().addToast('error', msg);
        });
    },
    onImport: () => {
      if (opts.onShowImportModal) {
        opts.onShowImportModal({
          connectionId,
          schema,
          tableName: table,
          tableColumns: opts.tableColumns,
        });
      } else {
        useToastStore.getState().addToast('info', 'Import is available from the sidebar for now.');
      }
    },
    onRename: async () => {
      const newName = await useDialogStore.getState().showPrompt({
        title: 'Rename Table',
        message: `Rename table "${table}" to:`,
        defaultValue: table,
      });
      if (newName?.trim() && newName.trim() !== table) {
        try {
          await apiClient.post(`/api/management/${connectionId}/rename-table`, {
            schema,
            oldName: table,
            newName: newName.trim(),
            database,
          });
          queryClient.invalidateQueries({ queryKey: ['schema', connectionId, database] });
          useToastStore.getState().addToast('success', `Renamed to ${newName.trim()}`);
        } catch (err: unknown) {
          const msg = err instanceof Error ? err.message : 'Rename failed';
          useToastStore.getState().addToast('error', msg);
        }
      }
    },
    onCopyTableName: () => {
      navigator.clipboard.writeText(qualifiedName);
      useToastStore.getState().addToast('success', 'Copied table name');
    },
    onCopySelectQuery: () => {
      navigator.clipboard.writeText(selectQuery);
      useToastStore.getState().addToast('success', 'Copied SELECT query');
    },
    onTruncate: () => {
      useDialogStore.getState().showConfirm({
        title: 'Truncate Table',
        message: `This will permanently delete all rows in "${schema}.${table}". The table structure will be preserved. This action cannot be undone.`,
        confirmText: table,
        danger: true,
        onConfirm: async () => {
          await apiClient.post(`/api/management/${connectionId}/truncate-table`, {
            schema,
            name: table,
            confirmName: table,
            database,
          });
          queryClient.invalidateQueries({ queryKey: ['schema', connectionId, database] });
          queryClient.invalidateQueries({ queryKey: ['table-data', connectionId, database, schema, table] });
          useToastStore.getState().addToast('success', `Truncated ${table}`);
        },
      });
    },
    onDrop: () => {
      useDialogStore.getState().showConfirm({
        title: 'Drop Table',
        message: `This will permanently delete the table "${schema}.${table}" and all its data. This action cannot be undone.`,
        confirmText: table,
        danger: true,
        onConfirm: async () => {
          await apiClient.post(`/api/management/${connectionId}/drop-table`, {
            schema,
            name: table,
            confirmName: table,
            database,
          });
          queryClient.invalidateQueries({ queryKey: ['schema', connectionId, database] });
          useToastStore.getState().addToast('success', `Dropped ${table}`);
        },
      });
    },
  };
}
